import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Dialog, Transition } from '@headlessui/react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { useNavigation, type NavItem } from '../../lib/useNavigation'

interface Command {
  id: string
  label: string
  group: string
  path: string
  icon: React.ComponentType<{ className?: string }>
  keywords?: string[]
}

function flattenNavToCommands(items: NavItem[]): Command[] {
  const commands: Command[] = []
  for (const item of items) {
    if (item.children) {
      for (const child of item.children) {
        commands.push({
          id: child.href.replace(/^\//, '') || 'home',
          label: child.name,
          group: item.name,
          path: child.href,
          icon: child.icon,
          keywords: child.keywords,
        })
      }
    } else if (item.href !== undefined && item.icon) {
      commands.push({
        id: item.href.replace(/^\//, '') || 'home',
        label: item.name,
        group: 'Navigation',
        path: item.href,
        icon: item.icon,
        keywords: item.keywords,
      })
    }
  }
  return commands
}

function fuzzyMatch(text: string, query: string): boolean {
  const lowerText = text.toLowerCase()
  const lowerQuery = query.toLowerCase()
  let qi = 0
  for (let ti = 0; ti < lowerText.length && qi < lowerQuery.length; ti++) {
    if (lowerText[ti] === lowerQuery[qi]) qi++
  }
  return qi === lowerQuery.length
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const commands = useMemo(() => flattenNavToCommands(navigation), [navigation])

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  const filtered = useMemo(() => {
    if (!query) return commands
    return commands.filter(
      (cmd) =>
        fuzzyMatch(cmd.label, query) ||
        cmd.keywords?.some((kw) => fuzzyMatch(kw, query)) ||
        fuzzyMatch(cmd.group, query)
    )
  }, [query, commands])

  const groups = useMemo(() => {
    const map = new Map<string, Command[]>()
    for (const cmd of filtered) {
      const list = map.get(cmd.group) || []
      list.push(cmd)
      map.set(cmd.group, list)
    }
    return map
  }, [filtered])

  const handleSelect = useCallback(
    (cmd: Command) => {
      setOpen(false)
      setQuery('')
      navigate(cmd.path)
    },
    [navigate]
  )

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={() => { setOpen(false); setQuery('') }} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-start justify-center pt-[20vh] p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-lg rounded-xl bg-white dark:bg-dark-surface shadow-2xl ring-1 ring-stone-200 dark:ring-stone-700 overflow-hidden">
              <div className="flex items-center gap-3 border-b border-stone-200 dark:border-stone-700 px-4">
                <MagnifyingGlassIcon className="h-5 w-5 text-stone-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search commands..."
                  className="flex-1 py-3 bg-transparent text-sm text-stone-900 dark:text-dark-text placeholder-stone-400 outline-none border-none focus:ring-0"
                  autoFocus
                />
                <kbd className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] font-medium text-stone-400 bg-stone-100 dark:bg-stone-800 rounded">
                  ESC
                </kbd>
              </div>
              <div className="max-h-72 overflow-y-auto p-2">
                {filtered.length === 0 ? (
                  <p className="py-6 text-center text-sm text-stone-500">No results found</p>
                ) : (
                  Array.from(groups.entries()).map(([group, cmds]) => (
                    <div key={group} className="mb-2">
                      <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-stone-400">{group}</p>
                      {cmds.map((cmd) => (
                        <button
                          key={cmd.id}
                          onClick={() => handleSelect(cmd)}
                          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-teal-50 dark:hover:bg-stone-700"
                        >
                          <cmd.icon className="h-4 w-4 text-stone-400" />
                          {cmd.label}
                        </button>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}
